import { useTranslation } from 'react-i18next';

import { Popover, Text, tokens, Tooltip } from '@hotjar/hotsauce';
import { localeNumber } from '@hotjar/insights-common/lib/number';
import { capitalize } from '@hotjar/insights-common/lib/string';
import { usePrettifyPrice } from '@hotjar/webapp/hooks/usePrettifyPrice';
import type { Site } from '@hotjar/site';
import { useHelpHotjarUrl } from '@hotjar/translations';
import { useProductPricing } from '@hotjar/webapp/features/Billing/Upsell/hooks/useProductPricing';

import { LoaderWrapper } from '~/trials/components/LoaderWrapper/LoaderWrapper';
import { useGetSiteProducts } from '~/trials/external-dependencies/domains/product/hooks/useGetSiteProducts';
import { useTrafficCoverage } from '~/trials/external-dependencies/hooks/useTrafficCoverage';
import { PlanSelection } from '~/trials/components/PlanSelection/PlanSelection';
import { MODAL, SESSIONS_WE_CAPTURE } from '~/trials/constants';
import { useTrack } from '~/trials/external-dependencies/domains/tracking/useTrack';
import {
  type Product,
  type AnalyticsProduct,
  type FeedbackProduct,
  type ProductCatalog,
  ProductType,
} from '~/trials/external-dependencies/domains/product/types';
import { usePlanRecommendations } from '~/trials/external-dependencies/domains/revenue/recommendations/hooks/usePlanRecommendations';
import { useEffectOnceWithDependencies } from '~/trials/external-dependencies/hooks/useEffectOnceWithDependencies';
import { PlanMonthlyCost } from './components/PlanMonthlyCost/PlanMonthlyCost';

import { SiteListItemStyles as S } from './SiteListItem.styles';

interface Props {
  site: Site;
  productCatalog: ProductCatalog | undefined;
  onPlanChange: (site: Site, plan: AnalyticsProduct | FeedbackProduct) => void;
  selectedProducts: {
    [productType in ProductType.Feedback | ProductType.Analytics]: Product | null | undefined;
  };
}

export const SiteListItem = ({
  site,
  productCatalog,
  onPlanChange,
  selectedProducts,
}: Props): JSX.Element => {
  const { t } = useTranslation('trials', { keyPrefix: 'post-trial.site-list' });
  const helpHotjarUrl = useHelpHotjarUrl();

  const canSelfServeObserve = selectedProducts[ProductType.Analytics]?.selfServeEnabled;
  const canSelfServeAsk = selectedProducts[ProductType.Feedback]?.selfServeEnabled;

  const { isLoading: isLoadingSiteProducts, feedbackProduct } = useGetSiteProducts(site);
  const { dailyTraffic } = useTrafficCoverage(site);
  const { isLoading: isLoadingRecommendations, products: recommendedProducts } =
    usePlanRecommendations(site);
  const { prettifyPrice } = usePrettifyPrice();
  const { click: trackClick, getGroupDataForSite } = useTrack();

  const isLoading = isLoadingRecommendations || isLoadingSiteProducts;

  const planChanged = (selectedPlan: Product) => {
    onPlanChange(site, selectedPlan);
    trackClick(MODAL.name, MODAL.selectDropdown, getGroupDataForSite(site), {
      sku: selectedPlan.sku,
    });
  };

  const selectedPlansAreEqualToRecommended = () => {
    const selectedAnalyticProductEqualToRecommended =
      selectedProducts[ProductType.Analytics]?.sku ===
      recommendedProducts[ProductType.Analytics]?.sku;
    const selectedFeedbackProductEqualToRecommended =
      selectedProducts[ProductType.Feedback]?.sku ===
      recommendedProducts[ProductType.Feedback]?.sku;
    return selectedAnalyticProductEqualToRecommended && selectedFeedbackProductEqualToRecommended;
  };

  useEffectOnceWithDependencies(
    () => {
      Object.values(recommendedProducts).forEach((recommendedProduct) => {
        if (recommendedProduct) {
          onPlanChange(site, recommendedProduct);
        }
      });
    },
    [site, recommendedProducts],
    () => !isLoading,
  );

  const { price: observePrice, isLoading: isLoadingObservePrice } = useProductPricing({
    site,
    product: selectedProducts[ProductType.Analytics],
    billingPeriod: 'MONTHLY',
  });

  const { price: askPrice, isLoading: isLoadingAskPrice } = useProductPricing({
    site,
    product: selectedProducts[ProductType.Feedback],
    billingPeriod: 'MONTHLY',
  });

  const analyticsSkuLocalePrice = prettifyPrice(observePrice?.withoutDiscounts ?? 0);
  const feedbackSkuLocalePrice = prettifyPrice(askPrice?.withoutDiscounts ?? 0);

  return (
    <S.Container>
      <S.SiteHeader>
        <S.SiteName>{site.name}</S.SiteName>
        <S.SiteDiscount>
          {t('bundle-discount')}
          <Tooltip text={t('bundle-discount', { context: 'tooltip' })}>
            <S.StyledIcon icon="info" size="m" />
          </Tooltip>
        </S.SiteDiscount>
      </S.SiteHeader>

      <S.Header>
        <S.HeaderColumn style={{ flexBasis: '15%' }} />
        <S.HeaderColumn style={{ flexBasis: '35%' }} />
        <S.HeaderColumn style={{ flexBasis: '30%' }}>
          {selectedPlansAreEqualToRecommended() ? t('headers.recommendations') : ''}
        </S.HeaderColumn>
        <S.HeaderColumn style={{ flexBasis: 'calc(20% - 48px)', textAlign: 'right' }}>
          {t('headers.cost-per-month')}
        </S.HeaderColumn>
      </S.Header>

      <S.Row style={{ borderTop: `1px solid ${tokens.color.ui_border_light}` }}>
        <S.Column style={{ flexBasis: '15%' }}>
          <Text textType="subtitle2">
            <strong>{t('observe-plan')}</strong>
            <Tooltip text={t('observe-plan', { context: 'tooltip' })}>
              <S.StyledIcon icon="info" size="m" />
            </Tooltip>
          </Text>
        </S.Column>

        <S.Column style={{ flexBasis: '35%' }}>
          <S.AllowanceLabel>{t('site-traffic-label')}</S.AllowanceLabel>
          <S.AllowanceValue>
            {t('site-traffic-value', {
              dailySessions: localeNumber(dailyTraffic),
            })}
          </S.AllowanceValue>
          <Popover
            title={t('site-traffic-value', { context: 'title' })}
            text={t('site-traffic-value', { context: 'text' })}
            actionText={t('site-traffic-value', { context: 'action' })}
            icon="info"
            onActionPress={() => {
              window.open(
                `${helpHotjarUrl}${SESSIONS_WE_CAPTURE}`,
                '_blank',
                'noopener noreferrer',
              );
            }}
          >
            <S.StyledIcon icon="info" size="m" isClickable />
          </Popover>
        </S.Column>

        <S.Column style={{ flexBasis: '30%' }}>
          <LoaderWrapper loading={!selectedProducts[ProductType.Analytics]}>
            {productCatalog != null && (
              <PlanSelection
                products={productCatalog.analyticsProducts}
                productType={ProductType.Analytics}
                selectedProductSKU={selectedProducts[ProductType.Analytics]?.sku}
                onPlanChange={planChanged}
                site={site}
              />
            )}
          </LoaderWrapper>
        </S.Column>

        <S.Column style={{ flexBasis: 'calc(20% - 48px)', textAlign: 'right' }}>
          {canSelfServeObserve ? (
            <PlanMonthlyCost loading={isLoadingObservePrice} price={analyticsSkuLocalePrice} />
          ) : (
            t('site-cost-non-self-serve')
          )}
        </S.Column>
      </S.Row>

      <S.Row>
        <S.Column style={{ flexBasis: '15%' }}>
          <Text textType="subtitle2">
            <strong>{t('ask-plan')}</strong>
            <Tooltip text={t('ask-plan', { context: 'tooltip' })}>
              <S.StyledIcon icon="info" size="m" />
            </Tooltip>
          </Text>
        </S.Column>

        <S.Column style={{ flexBasis: '35%' }}>
          <S.AllowanceLabel>{t('trial-plan-label')}</S.AllowanceLabel>
          <S.AllowanceValue>
            {t('trial-plan-value', {
              tier: capitalize(feedbackProduct?.tier),
              monthlyResponses: localeNumber(feedbackProduct?.monthlyResponses),
            })}
          </S.AllowanceValue>
        </S.Column>

        <S.Column style={{ flexBasis: '30%' }}>
          <LoaderWrapper loading={!selectedProducts[ProductType.Feedback]}>
            {productCatalog != null && (
              <PlanSelection
                products={productCatalog.feedbackProducts}
                productType={ProductType.Feedback}
                selectedProductSKU={selectedProducts[ProductType.Feedback]?.sku}
                onPlanChange={planChanged}
                site={site}
              />
            )}
          </LoaderWrapper>
        </S.Column>

        <S.Column style={{ flexBasis: 'calc(20% - 48px)', textAlign: 'right' }}>
          {canSelfServeAsk ? (
            <PlanMonthlyCost loading={isLoadingAskPrice} price={feedbackSkuLocalePrice} />
          ) : (
            t('site-cost-non-self-serve')
          )}
        </S.Column>
      </S.Row>
    </S.Container>
  );
};
